import { useState, useCallback } from 'react'
import { router } from '@inertiajs/react'
import { useTranslation } from 'react-i18next'
import { csrfHeaders } from '@/lib/csrf'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog'
import { Tabs, TabsList, TabsTrigger } from '../ui/tabs'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Loader2, CheckCircle2, AlertCircle, Github } from 'lucide-react'

interface ImportFromLovableDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

type ValidationState = 'idle' | 'validating' | 'valid' | 'error'
type ImportTab = 'lovable' | 'other'

const GITHUB_URL_REGEX = /^https:\/\/github\.com\/[^/]+\/[^/]+$/

export function ImportFromLovableDialog({
  open,
  onOpenChange,
}: ImportFromLovableDialogProps) {
  const { t } = useTranslation('projects')
  const [activeTab, setActiveTab] = useState<ImportTab>('lovable')
  const [githubUrl, setGithubUrl] = useState('')
  const [projectName, setProjectName] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [validationState, setValidationState] = useState<ValidationState>('idle')
  const [validationError, setValidationError] = useState('')
  const [repoInfo, setRepoInfo] = useState<{ name: string; description: string } | null>(null)
  const [installationId, setInstallationId] = useState<number | null>(null)
  const [needsInstallation, setNeedsInstallation] = useState(false)

  const resetState = () => {
    setGithubUrl('')
    setProjectName('')
    setActiveTab('lovable')
    setValidationState('idle')
    setValidationError('')
    setRepoInfo(null)
    setInstallationId(null)
    setNeedsInstallation(false)
  }

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) resetState()
    onOpenChange(isOpen)
  }

  const handleTabChange = (value: string) => {
    setActiveTab(value as ImportTab)
    setValidationState('idle')
    setValidationError('')
    setRepoInfo(null)
    setInstallationId(null)
    setNeedsInstallation(false)
  }

  const validateUrl = useCallback(async (url: string) => {
    if (!url.trim() || !GITHUB_URL_REGEX.test(url)) {
      setValidationState('idle')
      setRepoInfo(null)
      return
    }

    setValidationState('validating')
    setValidationError('')
    setRepoInfo(null)
    setInstallationId(null)
    setNeedsInstallation(false)

    try {
      const response = await fetch('/projects/validate_import_url', {
        method: 'POST',
        headers: csrfHeaders(),
        body: JSON.stringify({ github_url: url }),
      })

      const data = await response.json()

      if (response.ok && data.valid) {
        setValidationState('valid')
        setRepoInfo({ name: data.name, description: data.description || '' })
        setInstallationId(data.installation_id ?? null)
        if (!projectName) {
          setProjectName(data.name?.replace(/-/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase()) || '')
        }
      } else {
        setValidationState('error')
        setValidationError(data.error || t('import.errors.notFound'))
        setNeedsInstallation(data.needs_installation === true)
      }
    } catch {
      setValidationState('error')
      setValidationError(t('import.errors.networkError'))
    }
  }, [projectName, t])

  const handleUrlBlur = () => {
    validateUrl(githubUrl)
  }

  const handleUrlChange = (value: string) => {
    setGithubUrl(value)
    if (validationState !== 'idle') {
      setValidationState('idle')
      setRepoInfo(null)
      setInstallationId(null)
      setNeedsInstallation(false)
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (!githubUrl.trim() || isSubmitting) return

    if (!GITHUB_URL_REGEX.test(githubUrl)) {
      setValidationState('error')
      setValidationError(t('import.errors.invalidUrl'))
      return
    }

    setIsSubmitting(true)

    router.post('/projects/import_from_github', {
      import: {
        github_url: githubUrl.trim(),
        name: projectName.trim() || undefined,
        source: activeTab === 'lovable' ? 'lovable' : 'github',
        installation_id: installationId ?? undefined,
      },
    }, {
      onFinish: () => setIsSubmitting(false),
      onSuccess: () => {
        resetState()
        onOpenChange(false)
      },
    })
  }

  const handleInstallApp = () => {
    const returnUrl = encodeURIComponent(`${window.location.origin}/projects?open_import=true`)
    window.location.href = `/github_app/install?return_url=${returnUrl}`
  }

  const canSubmit = githubUrl.trim() && validationState === 'valid' && !isSubmitting

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{t('import.title')}</DialogTitle>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={handleTabChange}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="lovable">{t('import.tabs.lovable')}</TabsTrigger>
            <TabsTrigger value="other">{t('import.tabs.other')}</TabsTrigger>
          </TabsList>
        </Tabs>

        <p className="text-sm text-muted-foreground">
          {t(`import.${activeTab}.description`)}
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* GitHub URL */}
          <div className="space-y-2">
            <Label htmlFor="github-url">{t('import.urlLabel')}</Label>
            <div className="relative">
              <Input
                id="github-url"
                value={githubUrl}
                onChange={(e) => handleUrlChange(e.target.value)}
                onBlur={handleUrlBlur}
                placeholder={activeTab === 'lovable'
                  ? t('import.lovable.urlPlaceholder')
                  : t('import.other.urlPlaceholder')
                }
                disabled={isSubmitting}
                className={
                  validationState === 'valid'
                    ? 'border-green-500 pr-10'
                    : validationState === 'error'
                    ? 'border-destructive pr-10'
                    : ''
                }
              />
              {validationState === 'validating' && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                </div>
              )}
              {validationState === 'valid' && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                </div>
              )}
              {validationState === 'error' && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <AlertCircle className="h-4 w-4 text-destructive" />
                </div>
              )}
            </div>
            {validationState === 'validating' && (
              <p className="text-xs text-muted-foreground">{t('import.validating')}</p>
            )}
            {validationState === 'valid' && repoInfo && (
              <p className="text-xs text-green-600">
                {t('import.validSuccess')} {repoInfo.description && `— ${repoInfo.description}`}
              </p>
            )}
            {validationState === 'error' && needsInstallation && (
              <div className="rounded-lg border border-border bg-muted/50 p-3 space-y-2">
                <div className="flex items-center gap-2">
                  <Github className="h-4 w-4 text-muted-foreground shrink-0" />
                  <span className="text-sm font-medium">{t('import.privateRepo.title')}</span>
                </div>
                <p className="text-xs text-muted-foreground">{t('import.privateRepo.description')}</p>
                <Button type="button" size="sm" variant="outline" onClick={handleInstallApp}>
                  <Github className="mr-2 h-3.5 w-3.5" />
                  {t('import.privateRepo.button')}
                </Button>
              </div>
            )}
            {validationState === 'error' && !needsInstallation && (
              <p className="text-xs text-destructive">{validationError}</p>
            )}
            {validationState === 'idle' && (
              <p className="text-xs text-muted-foreground">{t('import.urlHelp')}</p>
            )}
          </div>

          {/* Project Name */}
          <div className="space-y-2">
            <Label htmlFor="project-name">{t('import.projectName')}</Label>
            <Input
              id="project-name"
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              placeholder={repoInfo?.name || ''}
              disabled={isSubmitting}
            />
          </div>

          {/* Submit */}
          <div className="flex justify-end pt-2">
            <Button type="submit" disabled={!canSubmit}>
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  {t('import.importing')}
                </>
              ) : (
                t('import.submit')
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
